import React, { useEffect } from 'react';
import { ArrowLeft, Download, FileText, Mail, Shield, Building2, Phone, Globe, CheckCircle2 } from 'lucide-react';
import ditaLogo from '../icons.png';

export default function MarketingConsentNotaInformarePage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Notă de informare prelucrare date CRM și Marketing | DITA ESTFARM';

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const pdfDownloadUrl = '/Nota_de_informare_DITA_ESTFARM_completa_v1.pdf';

  return (
    <main className="fixed inset-0 z-50 overflow-y-auto bg-slate-50 text-slate-900 py-10 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Navigation & Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 print:hidden">
          <a
            href="/marketing-consent"
            className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-xs border border-slate-200 hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft size={16} /> Înapoi la formularul de acord
          </a>

          <a
            href={pdfDownloadUrl}
            download="Nota_de_informare_DITA_ESTFARM_completa_v1.pdf"
            className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-blue-800 transition-all focus:ring-4 focus:ring-blue-100"
          >
            <Download size={16} /> Descarcă documentul oficial (.PDF)
          </a>
        </div>

        {/* Main Document Container */}
        <article className="rounded-3xl bg-white p-6 sm:p-12 shadow-sm border border-slate-200">
          {/* Brand header */}
          <header className="border-b border-slate-200 pb-8 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <img src={ditaLogo} alt="DITA ESTFARM" className="h-auto w-36" />
              <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-blue-700 border border-blue-200">
                <Shield size={14} /> Document Oficial Conform Legii RM 195/2024
              </div>
            </div>

            <p className="mt-6 text-xs font-bold uppercase tracking-widest text-blue-700">
              S.R.L. DITA ESTFARM • IDNO 1002600046359
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-950 sm:leading-tight">
              NOTĂ DE INFORMARE
            </h1>
            <p className="mt-2 text-base sm:text-lg font-medium text-slate-700">
              privind prelucrarea datelor profesionale ale medicilor, consultanților și farmaciștilor în CRM și comunicările de marketing direct
            </p>
            <p className="mt-2 text-xs font-semibold text-slate-400">
              Versiunea 1.0 • În conformitate cu Legea Republicii Moldova nr. 195 din 25.07.2024
            </p>
          </header>

          {/* Document Content */}
          <div className="mt-8 space-y-9 text-[15px] leading-relaxed text-slate-700">
            {/* 1 */}
            <section aria-labelledby="sec-1">
              <h2 id="sec-1" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">1</span>
                Cine prelucrează datele și cum ne puteți contacta
              </h2>
              <p className="mt-3">
                Operatorul de date cu caracter personal este <strong>S.R.L. DITA ESTFARM</strong>, IDNO <strong>1002600046359</strong>, cu sediul în mun. Chișinău, str. Burebista nr. 23, Republica Moldova (în continuare „Compania”).
              </p>
              <div className="mt-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 p-4 flex items-start gap-3">
                <Mail className="mt-0.5 text-blue-700 shrink-0" size={18} />
                <p className="text-sm">
                  Pentru întrebări privind protecția datelor sau pentru exercitarea drepturilor dumneavoastră, puteți contacta responsabilul cu protecția datelor (DPO) la adresa de email{' '}
                  <a href="mailto:dpo@dita.md" className="font-bold text-blue-700 underline">dpo@dita.md</a> sau puteți transmite o solicitare scrisă la sediul Companiei.
                </p>
              </div>
            </section>

            {/* 2 */}
            <section aria-labelledby="sec-2">
              <h2 id="sec-2" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">2</span>
                Cui se adresează această Notă
              </h2>
              <p className="mt-3">
                Prezenta Notă se adresează <strong>medicilor, consultanților și farmaciștilor</strong> ale căror date profesionale sunt utilizate de Companie în sistemul de planificare și raportare a vizitelor reprezentanților (CRM) și, separat, persoanelor care aleg să primească informații promoționale despre medicamente, produse cosmetice și alte produse din portofoliul Companiei.
              </p>
              <div className="mt-3.5 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-950">
                <p className="font-semibold">Separarea clară a scopurilor:</p>
                <p className="mt-1">
                  Evidența relației profesionale în CRM și expedierea comunicărilor de marketing direct reprezintă două scopuri distincte, cu temeiuri juridice diferite. Refuzul sau retragerea consimțământului pentru marketing nu determină, prin el însuși, încetarea prelucrărilor necesare gestionării relației profesionale și nici a celor impuse prin lege.
                </p>
              </div>
            </section>

            {/* 3 */}
            <section aria-labelledby="sec-3">
              <h2 id="sec-3" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">3</span>
                Ce date prelucrăm și din ce surse provin
              </h2>
              <p className="mt-3">
                În funcție de relația profesională și de opțiunile dumneavoastră, putem prelucra:
              </p>
              <ul className="mt-2.5 list-disc pl-6 space-y-1 text-sm text-slate-700">
                <li>Numele și prenumele;</li>
                <li>Profesia / specialitatea medicală sau farmaceutică;</li>
                <li>Unitatea medicală, unitatea de lucru, farmacia ori altă afiliere profesională;</li>
                <li>Adresa de e-mail profesională/personală (în cazul lipsei celei profesionale);</li>
                <li>Numărul de telefon profesional/personal (în cazul lipsei celui profesional);</li>
                <li>Date referitoare la vizitele și interacțiunile profesionale înregistrate în CRM;</li>
                <li>Ariile terapeutice selectate;</li>
                <li>Canalele de marketing preferate (E-mail, Telefon, SMS);</li>
                <li>Informațiile necesare demonstrării exprimării, confirmării, modificării sau retragerii consimțământului și a opoziției la marketing (inclusiv adresa IP, timestamp, hash criptografic).</li>
              </ul>
              <p className="mt-3 text-sm font-medium text-slate-800">
                Datele sunt obținute direct de la dumneavoastră, inclusiv prin interacțiunea cu reprezentanții Companiei sau prin formularul electronic de consimțământ. Nu colectăm date din alte surse.
              </p>
            </section>

            {/* 4 */}
            <section aria-labelledby="sec-4">
              <h2 id="sec-4" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">4</span>
                De ce utilizăm datele și în baza cărui temei juridic
              </h2>

              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-100 text-xs font-bold uppercase text-slate-700">
                    <tr>
                      <th className="px-4 py-3 sm:px-6">Scop / Operațiune</th>
                      <th className="px-4 py-3 sm:px-6">Temei juridic și explicație</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900 align-top">
                        Administrarea relației profesionale și a CRM
                      </td>
                      <td className="px-4 py-3.5 sm:px-6 align-top">
                        <strong>Art. 6 alin. (1) lit. f) din Legea nr. 195/2024</strong> — interesul legitim al Companiei de a organiza, planifica și documenta vizitele reprezentanților săi, de a menține evidența contactelor profesionale și de a gestiona comunicările operaționale. Acest interes se exercită sub rezerva respectării drepturilor dumneavoastră și a unui test de balanță.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900 align-top">
                        Marketing direct către profesioniști
                      </td>
                      <td className="px-4 py-3.5 sm:px-6 align-top">
                        <strong>Art. 6 alin. (1) lit. a) și art. 7 din Legea nr. 195/2024</strong> — consimțământul dumneavoastră pentru informații promoționale privind medicamente, produse cosmetice, alte produse din portofoliu, oferte, noutăți și invitații la evenimente promoționale, exclusiv pentru ariile și canalele selectate, în limitele legislației speciale în materia publicității.
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900 align-top">
                        Evidența consimțămintelor și a retragerilor
                      </td>
                      <td className="px-4 py-3.5 sm:px-6 align-top">
                        <strong>Art. 6 alin. (1) lit. c)</strong> — respectarea obligațiilor legale privind dovada consimțământului și exercitarea drepturilor; după caz, <strong>art. 6 alin. (1) lit. f)</strong> — protejarea drepturilor Companiei în cazul unor reclamații sau litigii, numai în măsura necesară.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-sm text-slate-600">
                Comunicările strict operaționale (de exemplu, referitoare la produse deja comandate sau aspecte logistice) sunt distincte de mesajele promoționale.
              </p>

              {/* Farmacovigilenta */}
              <div className="mt-4 rounded-2xl bg-blue-50/70 border border-blue-200/80 p-5 text-sm space-y-2">
                <p className="font-bold text-blue-950">Farmacovigilență:</p>
                <p>
                  Raportarea și gestionarea reacțiilor adverse se efectuează în temeiul obligației legale (<strong>art. 6 alin. (1) lit. c)</strong>). În scopul îndeplinirii obligațiilor legale și contractuale în domeniul farmacovigilenței, DITA ESTFARM poate prelucra date cu caracter personal ale medicilor, farmaciștilor și altor profesioniști din domeniul sănătății care raportează sau furnizează informații privind reacții adverse suspectate asociate utilizării medicamentelor.
                </p>
                <p>
                  Datele prelucrate pot include numele și prenumele raportorului, specialitatea sau funcția, instituția în care activează, datele de contact profesionale, precum și corespondența necesară documentării cazurilor. Prelucrarea se realizează conform Legii nr. 153 din 19 iunie 2025 cu privire la medicamente.
                </p>
                <p>
                  Datele pot fi transmise către Agenția Medicamentului și Dispozitivelor Medicale (AMDM) și deținătorii autorizațiilor de punere pe piață. Datele de farmacovigilență sunt gestionate strict separat de cele din CRM și marketing.
                </p>
              </div>
            </section>

            {/* 5 */}
            <section aria-labelledby="sec-5">
              <h2 id="sec-5" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">5</span>
                Cine poate primi datele dumneavoastră
              </h2>
              <p className="mt-3">
                <strong>În interiorul Companiei</strong>, accesul este limitat la personalul autorizat care are atribuții în gestionarea contactelor profesionale, a vizitelor, a marketingului, a sistemelor informatice și a solicitărilor privind protecția datelor.
              </p>
              <p className="mt-2">
                <strong>În afara Companiei</strong>, datele pot fi divulgate, numai în măsura necesară, către:
              </p>
              <ul className="mt-2 list-disc pl-6 space-y-1 text-sm text-slate-700">
                <li>(i) furnizori de administrare, găzduire și mentenanță CRM;</li>
                <li>(ii) furnizori de servicii de expediere e-mail și SMS sau suport comunicații, în calitate de persoane împuternicite;</li>
                <li>(iii) autorități publice și organe competente, atunci când divulgarea este impusă de lege.</li>
              </ul>
              <div className="mt-3.5 rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-sm font-medium text-slate-800">
                Alte entități ale Grupului DITA nu au acces la datele dumneavoastră. Datele dumneavoastră <strong>nu se comunică partenerilor Companiei în scop comercial</strong>.
              </div>
            </section>

            {/* 6 */}
            <section aria-labelledby="sec-6">
              <h2 id="sec-6" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">6</span>
                Transferuri în afara Republicii Moldova
              </h2>
              <p className="mt-3">
                În cadrul procesului de evidență a relațiilor profesionale în CRM și de transmitere a comunicărilor de marketing direct, <strong>S.R.L. DITA ESTFARM nu efectuează transferuri internaționale de date cu caracter personal</strong>.
              </p>
              <p className="mt-2 text-sm text-slate-600">
                În cazul în care, pe viitor, vor fi necesare transferuri de date în afara Republicii Moldova, Compania va asigura respectarea cerințelor Legii nr. 195/2024 și va informa persoanele vizate în prealabil cu privire la destinatarii, garanțiile aplicabile și modalitatea de obținere a unei copii a acestora.
              </p>
            </section>

            {/* 7 */}
            <section aria-labelledby="sec-7">
              <h2 id="sec-7" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">7</span>
                Cât timp păstrăm datele
              </h2>
              <p className="mt-3">
                Păstrăm datele numai atât timp cât este necesar scopului pentru care au fost colectate. La expirarea termenelor, datele sunt șterse ori anonimizate:
              </p>

              <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-100 text-xs font-bold uppercase text-slate-700">
                    <tr>
                      <th className="px-4 py-3 sm:px-6">Categoria</th>
                      <th className="px-4 py-3 sm:px-6">Durata / Criteriul de păstrare</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900">Datele profesionale CRM</td>
                      <td className="px-4 py-3.5 sm:px-6">Pe durata relației profesionale active; în lipsa de interacțiuni, datele se șterg/anonimizează după <strong>36 luni</strong> de la ultima interacțiune relevantă.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900">Preferințele și datele de marketing</td>
                      <td className="px-4 py-3.5 sm:px-6">Până la retragerea consimțământului sau până la opoziție, dar nu mai mult de <strong>36 luni</strong> de la ultima interacțiune.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900">Dovezi consimțământ și retragere</td>
                      <td className="px-4 py-3.5 sm:px-6">Datele strict necesare probării se păstrează <strong>36 luni</strong> după încetarea comunicărilor, în limita necesității demonstrării conformității.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3.5 sm:px-6 font-semibold text-slate-900">Evidența dezabonărilor</td>
                      <td className="px-4 py-3.5 sm:px-6">Evidență minimă, cu acces restrâns, păstrată <strong>36 luni</strong> pentru a evita reluarea comunicărilor nedorite.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* 8 */}
            <section aria-labelledby="sec-8">
              <h2 id="sec-8" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">8</span>
                Este obligatoriu să furnizați datele?
              </h2>
              <p className="mt-3">
                Furnizarea datelor în scop de marketing direct și acordarea consimțământului sunt <strong>voluntare</strong>. Refuzul nu afectează relația profesională cu Compania.
              </p>
              <p className="mt-2 text-sm text-slate-600">
                În cadrul relației profesionale, unele date de identificare și contact sunt necesare pentru organizarea vizitelor și a comunicărilor. Dacă nu sunt furnizate, Compania poate să nu poată realiza aceste activități prin modalitățile respective.
              </p>
            </section>

            {/* 9 */}
            <section aria-labelledby="sec-9">
              <h2 id="sec-9" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">9</span>
                Drepturile dumneavoastră
              </h2>
              <p className="mt-3">
                În condițiile Legii nr. 195/2024, beneficiați de următoarele drepturi:
              </p>
              <div className="mt-3 grid gap-2.5 sm:grid-cols-2 text-sm">
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Dreptul la informare și acces</strong> (art. 15)
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Dreptul la rectificare</strong> (art. 16)
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Dreptul la ștergerea datelor</strong> (art. 17)
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Restricționarea prelucrării</strong> (art. 18)
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Dreptul la portabilitate</strong> (art. 20)
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <strong>• Dreptul de opoziție</strong> (art. 21)
                </div>
              </div>
              <p className="mt-3 text-sm">
                În cazul prelucrării bazate pe interes legitim, vă puteți opune din motive care țin de situația dumneavoastră particulară. În schimb, <strong>dreptul de opoziție la marketingul direct este necondiționat</strong>: dacă vă opuneți, datele nu vor mai fi prelucrate în acest scop.
              </p>
              <p className="mt-2 text-sm">
                Ne puteți contacta oricând la <a href="mailto:dpo@dita.md" className="font-bold text-blue-700 underline">dpo@dita.md</a>. Răspundem de regulă în cel mult o lună de la primirea cererii.
              </p>
            </section>

            {/* 10 */}
            <section aria-labelledby="sec-10">
              <h2 id="sec-10" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">10</span>
                Consimțământul pentru marketing și retragerea lui
              </h2>
              <p className="mt-3">
                Marketingul direct se desfășoară doar pentru ariile terapeutice și canalele de comunicare alese în mod expres de dumneavoastră. <strong>Nicio opțiune nu este preselectată.</strong>
              </p>
              <div className="mt-3.5 rounded-2xl bg-blue-50 border border-blue-200 p-4 text-sm space-y-2 text-blue-950">
                <p>
                  Vă puteți retrage sau modifica oricând consimțământul, integral ori parțial, pentru una sau mai multe arii terapeutice sau canale de comunicare, fără prezentarea unor justificări, prin linkul de dezabonare/gestionare a preferințelor din e-mailurile promoționale, accesând linkul{' '}
                  <a href="/marketing-consent/unsubscribe" className="font-bold text-blue-800 underline">https://crm.dita.md/marketing-consent/unsubscribe</a>{' '}
                  (se introduce email-ul, după care pe email vine codul de confirmare, se introduce codul și se apasă dezabonare), prin e-mail la{' '}
                  <a href="mailto:dpo@dita.md" className="font-bold text-blue-800 underline">dpo@dita.md</a> sau prin reprezentantul medical (în formă scrisă).
                </p>
                <p>
                  Comunicările de marketing încetează în termen de <strong>30 zile</strong> de la înregistrarea retragerii. Retragerea nu afectează legalitatea prelucrărilor desfășurate anterior retragerii.
                </p>
              </div>
            </section>

            {/* 11 */}
            <section aria-labelledby="sec-11">
              <h2 id="sec-11" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">11</span>
                Decizii automatizate și particularități ale comunicărilor
              </h2>
              <p className="mt-3">
                Preferințele privind ariile terapeutice și canalele sunt utilizate exclusiv pentru a limita comunicările la subiectele și mijloacele de contact solicitate. Compania nu ia decizii bazate exclusiv pe prelucrare automatizată și nu creează profiluri cu efecte juridice ori similar semnificative.
              </p>
            </section>

            {/* 12 */}
            <section aria-labelledby="sec-12">
              <h2 id="sec-12" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">12</span>
                Dreptul de a depune plângere la autoritate
              </h2>
              <p className="mt-3">
                Dacă considerați că prelucrarea datelor dumneavoastră încalcă legislația, aveți dreptul de a depune o plângere la autoritatea de supraveghere potrivit art. 72 din Legea nr. 195/2024, fără a fi obligatoriu să ne contactați în prealabil:
              </p>

              {/* CNPDCP Contact Card */}
              <div className="mt-4 rounded-2xl bg-slate-50 border border-slate-200 p-5 space-y-2 text-sm text-slate-800">
                <p className="font-bold text-slate-950 text-base">Centrul Național pentru Protecția Datelor cu Caracter Personal (CNPDCP)</p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Building2 size={16} className="text-blue-700 shrink-0" />
                  mun. Chișinău, str. Serghei Lazo nr. 48, MD-2004
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Mail size={16} className="text-blue-700 shrink-0" />
                  <a href="mailto:centru@datepersonale.md" className="font-semibold text-blue-700 underline">centru@datepersonale.md</a>
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Phone size={16} className="text-blue-700 shrink-0" />
                  +373 22 820 801
                </p>
                <p className="flex items-center gap-2 text-slate-600">
                  <Globe size={16} className="text-blue-700 shrink-0" />
                  <a href="https://www.datepersonale.md" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 underline">www.datepersonale.md</a>
                </p>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                Vă puteți adresa și instanței judecătorești competente, în condițiile art. 74 din aceeași lege.
              </p>
            </section>

            {/* 13 */}
            <section aria-labelledby="sec-13" className="border-t border-slate-200 pt-6">
              <h2 id="sec-13" className="text-lg font-bold text-slate-950 flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-100 text-blue-800 text-xs font-black">13</span>
                Disponibilitatea și actualizarea Notei
              </h2>
              <p className="mt-3">
                Nota este disponibilă permanent la adresa <a href="/marketing-consent/nota-de-informare" className="font-bold text-blue-700 underline">https://crm.dita.md/marketing-consent/nota-de-informare</a>, cu trimitere din formularul electronic de consimțământ, din pagina de colectare și din e-mailurile de marketing.
              </p>
              <p className="mt-2 text-sm text-slate-500 font-medium">
                Versiunea 1.0 din 2026. Informarea scurtă din formular rămâne disponibilă separat.
              </p>
            </section>
          </div>

          {/* Bottom Actions */}
          <div className="mt-10 border-t border-slate-200 pt-6 flex flex-wrap items-center justify-between gap-4 print:hidden">
            <a
              href="/marketing-consent"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
            >
              <ArrowLeft size={16} /> Înapoi la formularul de acord
            </a>

            <a
              href={pdfDownloadUrl}
              download="Nota_de_informare_DITA_ESTFARM_completa_v1.pdf"
              className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-blue-800 transition-all"
            >
              <Download size={16} /> Descarcă documentul oficial (.PDF)
            </a>
          </div>
        </article>

        {/* Footer */}
        <footer className="mt-8 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} S.R.L. DITA ESTFARM • Toate drepturile rezervate conform Legii RM nr. 195/2024.
        </footer>
      </div>
    </main>
  );
}
